import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "app/store";
import { selectCurrentPractice } from "features/auth/auth-slice";

import type { EventsArgs } from "../data/api/events.api";
import type { FormPolicy } from "../domain/form-rules";
import type {
  CardScheme,
  ColumnShading,
  FormLayout,
  PreviewMode,
  SchedulerView
} from "../domain/settings";
import { type HoverGate, hoverGate } from "../engine/hover-gate";
import { visibleRange } from "../engine/range";
import type {
  PendingMove,
  SettingsHoldReason,
  SettingsSyncStatus
} from "./scheduler-v3.slice";

export const selectSettings = (s: RootState) => s.schedulerV3.settings;
export const selectSelectedDate = (s: RootState) => s.schedulerV3.selectedDate;
export const selectDefaultsApplied = (s: RootState) =>
  s.schedulerV3.defaultsApplied;
/** ENG-2146: the clinic filter needs its first-visit defaults after a staff-only reload. */
export const selectClinicDefaultsDue = (s: RootState) =>
  s.schedulerV3.clinicDefaultsDue;
export const selectPendingUndo = (s: RootState) => s.schedulerV3.pendingUndo;
export const selectStatusError = (s: RootState) => s.schedulerV3.statusError;
export const selectPendingMove = (s: RootState) => s.schedulerV3.pendingMove;
/** Moves sent to the server and still drawn over the cache (`SentMove`), oldest first. */
export const selectSentMoves = (s: RootState) => s.schedulerV3.sentMoves;
/** The newest move whose PATCH is still in flight, or null. */
export const selectSavingMove = (s: RootState): PendingMove | null => {
  const sent = s.schedulerV3.sentMoves;
  for (let i = sent.length - 1; i >= 0; i -= 1)
    if (!sent[i].saved) return sent[i].move;
  return null;
};

/**
 * Every move to draw over the cached rows, newest first: the one held behind Undo, then
 * each sent one the server's rows may not have yet.
 */
export const selectMovesToDraw = createSelector(
  [selectPendingMove, selectSentMoves],
  (held, sent): PendingMove[] => [
    ...(held ? [held] : []),
    ...sent.map((s) => s.move).reverse()
  ]
);

/**
 * Sweep's hide keys, by day (ENG-2104 hardening). The queue's items can span more than one
 * date (Sweep reads whatever the grid has loaded), so the page reads the whole map once and
 * looks a date up per item rather than calling a per-date selector in a loop.
 */
export const selectSweepSkippedByDate = (s: RootState) =>
  s.schedulerV3.sweepSkipped;
/**
 * Sweep's handled-row records, by day (D53): written once the problem left Radar, never
 * hidden, and forgotten when the item comes back.
 */
export const selectSweepHandledByDate = (s: RootState) =>
  s.schedulerV3.sweepHandled;
/** What S / Later put off, by day, as hide keys (D52). Neither hidden nor counted. */
export const selectSweepLaterByDate = (s: RootState) =>
  s.schedulerV3.sweepLater;
/** The bookings this pass's rebooks created (D58, WB-6). */
export const selectSweepCreated = (s: RootState) => s.schedulerV3.sweepCreated;
/** Sweep is on screen (D55). */
export const selectSweepActive = (s: RootState) => s.schedulerV3.sweepActive;
const EMPTY_SWEEP_SKIPPED: string[] = [];
export const selectSweepSkipped = (s: RootState, date: string) =>
  s.schedulerV3.sweepSkipped[date] ?? EMPTY_SWEEP_SKIPPED;

/**
 * `selectCurrentPractice`, resolved when the selector runs rather than when this module loads.
 * This file sits on an import cycle (auth-slice -> ... -> app/store -> scheduler-v3.slice -> here
 * -> auth-slice). Handing the imported binding straight to `createSelector` captured it while
 * auth-slice could still be mid-evaluation, so `createSelector` received `undefined` and threw
 * whenever the cycle was entered from the auth side (ENG-2235).
 */
const selectPractice = (state: RootState) => selectCurrentPractice(state);

/**
 * Whether completing an appointment has to collect an outcome.
 *
 * `shouldCollectOutcome` (`utils/appointments.util.ts:281`) reads this off the PRACTICE,
 * and every live call site passes `currentPractice?.collect_outcome`. The field is
 * declared on `services/types/clinics.type.ts:61` — a pre-existing type gap, not a clinic
 * rule, and not one to fix from here.
 *
 * Read from auth rather than widening the V3 catalog fetch: the value is already in the
 * store on every page, and a second source for it is a second thing to keep in sync.
 */
export const selectPracticeCollectsOutcome = createSelector(
  selectPractice,
  (practice) => Boolean(practice?.collect_outcome)
);

/** The events cache key for a view, its clinics and a date. See `selectEventsArgs`. */
export const eventsArgsFor = (
  view: SchedulerView,
  clinicIds: readonly number[],
  date: string
): EventsArgs => ({
  ...visibleRange(view, date),
  clinicIds: [...clinicIds].sort((a, b) => a - b)
});

/**
 * The events cache key. Only view, date and clinics reach the network: providers, types
 * and the Show chips filter locally, so toggling them is instant. Clinic ids are sorted
 * so the same selection in a different order hits the same cache entry.
 */
export const selectEventsArgs = createSelector(
  [
    (s: RootState) => s.schedulerV3.settings.view,
    (s: RootState) => s.schedulerV3.settings.clinicIds,
    selectSelectedDate
  ],
  (view, clinicIds, date) => eventsArgsFor(view, clinicIds, date)
);
export const selectCreateDraft = (s: RootState) => s.schedulerV3.createDraft;
export const selectEditAppointmentId = (s: RootState) =>
  s.schedulerV3.editAppointmentId;
export const selectRescheduleSourceId = (s: RootState) =>
  s.schedulerV3.rescheduleSourceId;
/**
 * The form is open in any of its modes.
 *
 * `editScheduleId` is not optional here: `openEditSchedule` sets it and then CLEARS
 * `createDraft` and `editAppointmentId`, so leaving it out of this selector made the
 * action's own last two lines guarantee the sheet stayed shut. Both entry points — the
 * preview's pencil and the grid's E key — were inert, and 587 tests were green because
 * every piece was tested in isolation and nothing asserted that the sheet OPENS.
 */
export const selectFormOpen = (s: RootState) =>
  Boolean(s.schedulerV3.createDraft) ||
  s.schedulerV3.editAppointmentId !== null ||
  s.schedulerV3.editScheduleId !== null;

export const selectEditScheduleId = (s: RootState) =>
  s.schedulerV3.editScheduleId;
export const selectEditKind = (s: RootState) => s.schedulerV3.editKind;
/** Whether the Booking Desk layout and its switch are offered (ENG-2218). */
export const selectBookingDeskOffered = (s: RootState): boolean =>
  s.schedulerV3.bookingDeskOffered;
/** SN-19653: whether a hand-marked visit counts as an Opportunity here. */
export const selectOpportunityFieldEnabled = (s: RootState): boolean =>
  s.schedulerV3.opportunityFieldEnabled;

/**
 * The layout the form opens in: the person's own pick, switchable from the form header —
 * while the Booking Desk is offered. Without it the form is classic, even over a stored desk
 * pick, and the pick is kept for when it comes back (ENG-2218, the toggle 2 shape of ENG-2145).
 */
export const selectFormLayout = (s: RootState): FormLayout =>
  s.schedulerV3.bookingDeskOffered
    ? s.schedulerV3.settings.formLayout
    : "classic";

/**
 * The scheme a card is drawn in: the person's pick in the Schedule settings drawer, filled
 * by default (ENG-2194). Read through here rather than `settings.cardScheme` so the one
 * place a future gate would go stays one place.
 */
export const selectCardScheme = (s: RootState): CardScheme =>
  s.schedulerV3.settings.cardScheme;

/** Tinted provider columns (the default) or plain white ones, as Classic View draws (SN-20010). */
export const selectColumnShading = (s: RootState): ColumnShading =>
  s.schedulerV3.settings.columnShading ?? "provider";

export const selectWorkShiftMode = (s: RootState) =>
  s.schedulerV3.workShiftMode;

/** The active quick filter, or `null`. ENG-2041. */
export const selectQuickFilterCode = (s: RootState) =>
  s.schedulerV3.quickFilterCode;

/** ENG-2032. `null` unless a patient or a reschedule owns the next slot click. */
export const selectSchedulingFor = (s: RootState) =>
  s.schedulerV3.schedulingFor;
/** The chart the user is owed a trip back to, set by the exit that owes it. */
export const selectSchedulingForReturn = (s: RootState) =>
  s.schedulerV3.schedulingForReturn;

export const selectPreviewEventId = (s: RootState) =>
  s.schedulerV3.previewEventId;
export const selectPreviewOpen = (s: RootState) =>
  s.schedulerV3.previewEventId !== null;
/** A setting, not transient state: the last choice sticks. See `SchedulerSettings`. */
export const selectPreviewMode = (s: RootState) =>
  s.schedulerV3.settings.previewMode;
/** The rail takes width from the grid only when a card is actually docked in it. */
/**
 * Where the card actually goes: the user's choice, unless the page has no room to dock
 * it, in which case it floats rather than leave the grid under its floor (ENG-2009). In
 * Sweep it always floats, whatever the saved choice (D55); the choice itself is untouched,
 * so it is back once Sweep closes.
 */
export const selectEffectivePreviewMode = (s: RootState): PreviewMode =>
  s.schedulerV3.settings.previewMode === "docked" &&
  s.schedulerV3.canDock &&
  !s.schedulerV3.sweepActive
    ? "docked"
    : "floating";

/** The user wants the rail but the window is too narrow for it: Dock is offered, disabled. */
export const selectDockBlocked = (s: RootState) =>
  s.schedulerV3.settings.previewMode === "docked" && !s.schedulerV3.canDock;

export const selectPreviewDocked = (s: RootState) =>
  s.schedulerV3.previewEventId !== null &&
  selectEffectivePreviewMode(s) === "docked";

/**
 * Whether `eventId`'s hover card may show over the details card (ENG-2282). A primitive per
 * event, so a hover card re-renders only when its own answer changes.
 */
export const selectHoverGate = (s: RootState, eventId: string): HoverGate =>
  hoverGate(
    eventId,
    s.schedulerV3.previewEventId,
    selectEffectivePreviewMode(s)
  );

/**
 * What the practice has switched on for the appointment form, as plain data. The rules
 * in `domain/form-rules.ts` take this as an argument so none of them has to reach for a
 * hook — and so a test can state the practice instead of mocking one.
 */
export const selectFormPolicy = createSelector(
  selectPractice,
  (practice): FormPolicy => ({
    // Settings → Practice → General → Required fields. Referral used to be an LD flag that
    // V3 never read, so it booked without one where classic refused.
    referralSourceRequired: Boolean(
      practice?.appointment_settings?.required_fields?.referral_source
    ),
    subReferralSourceRequired: Boolean(
      practice?.appointment_settings?.required_fields?.sub_referral_source
    ),
    subReferralSourceAvailable: false,
    overlapAlwaysAllowed: Boolean(
      (practice as unknown as { scheduling_allow_overlap?: boolean })
        ?.scheduling_allow_overlap
    ),
    ignoreRoomReservation: Boolean(
      (practice as unknown as { scheduling_ignore_room_reservation?: boolean })
        ?.scheduling_ignore_room_reservation
    )
  })
);

/** Practice `limit_to_current_clinic` scopes patient search to one clinic. */
export const selectLimitPatientsToClinic = createSelector(
  selectPractice,
  (practice) =>
    Boolean(
      (practice as unknown as { limit_to_current_clinic?: boolean })
        ?.limit_to_current_clinic
    )
);

/**
 * Practice `show_deactivated_patients_in_search` lists deactivated patients too, struck
 * through with a "Deactivated" chip, as classic's patient fields do (ENG-2573).
 */
export const selectShowDeactivatedPatientsInSearch = createSelector(
  selectPractice,
  (practice) =>
    Boolean(
      (practice as unknown as { show_deactivated_patients_in_search?: boolean })
        ?.show_deactivated_patients_in_search
    )
);

/**
 * Practice `use_room_traffic` offers the Room Traffic page (ENG-2019) — the old side
 * sheet's gate (`scheduler-side-sheet-collapsible.component.tsx:165`). Absent is off.
 */
export const selectUseRoomTraffic = createSelector(selectPractice, (practice) =>
  Boolean(practice?.use_room_traffic)
);

/* ---------------------------------------------------------------- ENG-2047 */

export const selectSettingsSync = (s: RootState) => s.schedulerV3.settingsSync;

/** A document has been applied, or the read is over. Until then the grid waits. */
const SETTLED: readonly SettingsSyncStatus[] = [
  "cacheHydrated",
  "hydrated",
  "readFailed",
  "timedOut"
];

/**
 * Whether the grid may lay itself out yet (§6.1). Only the grid route waits on this:
 * View Schedule, the Day Sheet and Room Traffic lay out nothing this decides.
 */
export const selectSettingsSettled = (s: RootState) =>
  SETTLED.includes(s.schedulerV3.settingsSync.status);

export const selectSettingsNotice = (s: RootState) =>
  s.schedulerV3.settingsSync.settingsNotice;

export const selectSettingsTouched = (s: RootState) =>
  s.schedulerV3.settingsSync.touched;

/** The three reasons a change cannot be sent at all. A failing write is not one. */
const BLOCKING: readonly SettingsHoldReason[] = [
  "unavailable",
  "readFailed",
  "versionHold"
];

export const selectSettingsWritesHeld = (s: RootState) => {
  const { holdReason } = s.schedulerV3.settingsSync;
  return holdReason !== null && BLOCKING.includes(holdReason);
};

/**
 * What the side panel says about the store, or `null` for the silent cases.
 *
 * A 404 says nothing: an older backend simply has no store, nothing has gone wrong, and a
 * caption would be the only thing the user ever heard about a feature they never had.
 */
export const selectSettingsCaption = createSelector(
  selectSettingsSync,
  ({ holdReason, touched }): string | null => {
    if (holdReason === "versionHold") return "Reload to save scheduler changes";
    if (
      (holdReason === "readFailed" || holdReason === "writeFailed") &&
      touched.length > 0
    ) {
      return "Changes not saved yet";
    }
    return null;
  }
);
/**
 * ENG-2104's LaunchDarkly switch, as the route boundary handed it in. The one read path for
 * the flag: components through `useAppSelector`, thunks through `getState()`.
 */
export const selectRadarEnabled = (s: RootState) => s.schedulerV3.radarEnabled;
/**
 * Radar (ENG-2104). A setting, so it survives a trip to a chart and back. Behind the flag:
 * the setting is shared across computers, so a stored `true` from a flag-on session opens
 * nothing while the flag is off.
 */
export const selectRadarOpen = (s: RootState) =>
  selectRadarEnabled(s) && s.schedulerV3.settings.radarOpen;
/** The cell a Radar row just drilled to; the grid scrolls to it, pulses it and clears it. */
export const selectFlashEventId = (s: RootState) => s.schedulerV3.flashEventId;
// No Radar domain here: this module is in the app's main bundle (`main-bundle.test.ts`).
